import { getTopLevelItems, normalizeMap, parseShaperChildrenBlob } from "../../helpers/shaper_children.js";

function asObject(value) {
    if (value === null || value === undefined) return {};
    const normalized = normalizeMap(value);
    if (normalized && typeof normalized === "object" && !Array.isArray(normalized)) {
        return normalized;
    }
    return {};
}

function asArray(value) {
    return Array.isArray(value) ? value : [];
}

function tupleItem(value, index) {
    const normalized = normalizeMap(value);
    if (Array.isArray(normalized)) {
        return normalized[index];
    }
    if (normalized && typeof normalized === "object") {
        if (Object.prototype.hasOwnProperty.call(normalized, index)) {
            return normalized[index];
        }
        if (Object.prototype.hasOwnProperty.call(normalized, String(index))) {
            return normalized[String(index)];
        }
        if (index === 0) {
            return normalized;
        }
    }
    return null;
}

function toNumber(value) {
    if (value === null || value === undefined) return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
}

export function latestValue(values) {
    if (!Array.isArray(values)) return null;
    for (let i = values.length - 1; i >= 0; i -= 1) {
        const n = toNumber(values[i]);
        if (n !== null) return n;
    }
    return null;
}

function normalizeSeries(values) {
    if (!Array.isArray(values)) return [];
    return values.map((v) => {
        const n = toNumber(v);
        return n === null ? null : n;
    });
}

// Re-align a series onto the heatmap axis ticks by timestamp, so series that
// carry their own tick list (QoO) cannot shift into neighbouring buckets.
// Returns one entry per axis tick: the source value at that exact tick, else
// null. Non-finite ticks/values are ignored.
export function alignSeriesToTicks(axisTicks, sourceTicks, values) {
    const axis = Array.isArray(axisTicks) ? axisTicks : [];
    const sources = Array.isArray(sourceTicks) ? sourceTicks : [];
    const series = Array.isArray(values) ? values : [];
    const byTick = new Map();
    for (let i = 0; i < sources.length; i += 1) {
        const tick = toNumber(sources[i]);
        if (tick === null || byTick.has(tick)) continue; // first value wins on duplicate ticks
        byTick.set(tick, i);
    }
    return axis.map((tick) => {
        const t = toNumber(tick);
        if (t === null || !byTick.has(t)) return null;
        const value = series[byTick.get(t)];
        return toNumber(value);
    });
}

function normalizePercentSeries(values) {
    return normalizeSeries(values).map((value) => (value === null ? null : value / 100));
}

function latestPercentValue(values) {
    const value = latestValue(values);
    return value === null ? null : value / 100;
}

function normalizedHashCandidates(rawHash) {
    const values = [];
    if (rawHash === null || rawHash === undefined) {
        return values;
    }
    const stringHash = String(rawHash);
    values.push(stringHash);
    values.push(rawHash);
    const numericHash = toNumber(rawHash);
    if (numericHash !== null) {
        values.push(numericHash);
        values.push(String(numericHash));
    }
    return values;
}

function lookupByHash(map, rawHash) {
    const source = asObject(map);
    const candidates = normalizedHashCandidates(rawHash);
    for (let i = 0; i < candidates.length; i += 1) {
        const key = candidates[i];
        if (Object.prototype.hasOwnProperty.call(source, key)) {
            return source[key] || {};
        }
    }
    // 64-bit precision fallback: the map is keyed by the exact id, but rawHash
    // may have been rounded to a JS number by CBOR decoding. Compare numerically
    // so both sides round the same way.
    const wanted = toNumber(rawHash);
    if (wanted !== null) {
        const keys = Object.keys(source);
        for (let i = 0; i < keys.length; i += 1) {
            const candidate = toNumber(keys[i]);
            if (candidate !== null && candidate === wanted) {
                return source[keys[i]] || {};
            }
        }
    }
    return {};
}

function utilizationFromThroughputSeries(throughputSeries, capacitySeries, throughputUnit, capacityUnit) {
    const throughput = normalizeSeries(throughputSeries);
    const capacity = normalizeSeries(capacitySeries);
    const len = Math.max(throughput.length, capacity.length);
    const result = [];
    const throughputBitsMultiplier = throughputUnit === "mib_per_second"
        ? 8 * 1024 * 1024
        : 8;
    for (let i = 0; i < len; i += 1) {
        const t = throughput[i] ?? null;
        const c = capacity[i] ?? null;
        if (t === null || c === null || c <= 0) {
            result.push(null);
            continue;
        }
        const bitsPerSecond = t * throughputBitsMultiplier;
        const capacityBitsPerSecond = capacityUnit === "mbps" ? c * 1_000_000 : c;
        if (!Number.isFinite(bitsPerSecond) || bitsPerSecond < 0) {
            result.push(null);
            continue;
        }
        if (!Number.isFinite(capacityBitsPerSecond) || capacityBitsPerSecond <= 0) {
            result.push(null);
            continue;
        }
        result.push((bitsPerSecond / capacityBitsPerSecond) * 100);
    }
    return result;
}

function utilizationThroughputFor(body, direction) {
    if (direction === "down") {
        if (Array.isArray(body.bytes_per_second_down_median)) {
            return { series: body.bytes_per_second_down_median, unit: "bytes_per_second" };
        }
        return { series: body.download_bytes_median, unit: "mib_per_second" };
    }
    if (Array.isArray(body.bytes_per_second_up_median)) {
        return { series: body.bytes_per_second_up_median, unit: "bytes_per_second" };
    }
    return { series: body.upload_bytes_median, unit: "mib_per_second" };
}

function rowUtilizationSeries(body) {
    const downInfo = utilizationThroughputFor(body, "down");
    const upInfo = utilizationThroughputFor(body, "up");
    const down = utilizationFromThroughputSeries(downInfo.series, body.capacity_down_mbps, downInfo.unit, "mbps");
    const up = utilizationFromThroughputSeries(upInfo.series, body.capacity_up_mbps, upInfo.unit, "mbps");

    const hasBytesBased = down.some((v) => Number.isFinite(v)) || up.some((v) => Number.isFinite(v));
    if (hasBytesBased) {
        return { down, up };
    }

    return {
        down: normalizePercentSeries(body.capacity_down_percent_median || body.capacity_down_percent),
        up: normalizePercentSeries(body.capacity_up_percent_median || body.capacity_up_percent),
    };
}

function executiveRowCategory(kind) {
    return kind === "Circuit" ? "Circuit" : "Site";
}

function executiveBadge(kind, header) {
    if (kind === "Circuit") {
        return "Circuit";
    }
    const siteType = typeof header?.site_type === "string" ? header.site_type.trim() : "";
    return siteType || "Site";
}

function rowFromNode(nodeHash, nodeInfo, qooByHash, kind, utilizationMode = "percent") {
    const header = nodeInfo?.header || {};
    const body = nodeInfo?.body || {};
    const rawHash = nodeHash ?? header.hash ?? 0;
    const hash = String(rawHash);
    const qooRow = lookupByHash(qooByHash, rawHash);

    const ticks = normalizeSeries(body.ticks);
    const rtt = normalizeSeries(body.median_rtt_median || body.rtt_median);
    const retransDown = normalizePercentSeries(body.retransmits_down_percent || body.tcp_retransmits_down_percent);
    const retransUp = normalizePercentSeries(body.retransmits_up_percent || body.tcp_retransmits_up_percent);
    const util = rowUtilizationSeries(body);
    const utilDown = util.down;
    const utilUp = util.up;
    const qooTicks = normalizeSeries(qooRow.ticks);
    const qooDown = alignSeriesToTicks(ticks, qooTicks, normalizeSeries(qooRow.qoo_down));
    const qooUp = alignSeriesToTicks(ticks, qooTicks, normalizeSeries(qooRow.qoo_up));

    const isCircuit = kind === "Circuit";
    const link = isCircuit
        ? `shaper_circuit.html?circuit=${encodeURIComponent(hash)}&nodeId=${encodeURIComponent(window.nodeId || "")}`
        : `shaper_site.html?site=${encodeURIComponent(hash)}&nodeId=${encodeURIComponent(window.nodeId || "")}`;

    return {
        hash,
        label: header.name || (isCircuit ? `Circuit ${hash}` : `Site ${hash}`),
        badge: executiveBadge(kind, header),
        row_type: executiveRowCategory(kind),
        link,
        ticks,
        metrics: {
            rtt_down: rtt,
            rtt_up: rtt,
            retransmit_down: retransDown,
            retransmit_up: retransUp,
            utilization_down: utilDown,
            utilization_up: utilUp,
            qoo_down: qooDown,
            qoo_up: qooUp,
        },
    };
}

function buildShaperExecutiveData(state) {
    const shaperMap = asObject(state.shaperDetail);
    const shaperInfo = Object.values(shaperMap)[0];
    if (!shaperInfo) return null;

    const shaperBody = shaperInfo.body || {};
    const ticks = normalizeSeries(shaperBody.ticks);
    const shaperUtil = rowUtilizationSeries(shaperBody);
    const shaperQoo = asObject(state.shaperQoo);
    const executivePayload = asObject(state.shaperExecutive);

    let topLevel = {};
    let children = {};
    let circuits = {};
    let siteQoo = {};
    let circuitQoo = {};
    let isFlat = false;

    if (
        Object.prototype.hasOwnProperty.call(executivePayload, "top_level")
        || Object.prototype.hasOwnProperty.call(executivePayload, "circuits")
    ) {
        topLevel = asObject(executivePayload.top_level);
        children = asObject(executivePayload.children);
        circuits = asObject(executivePayload.circuits);
        siteQoo = asObject(executivePayload.site_qoo);
        circuitQoo = asObject(executivePayload.circuit_qoo);
        isFlat = !!executivePayload.is_flat;
    } else {
        const parsedChildren = state.shaperChildren || parseShaperChildrenBlob({});
        const childData = getTopLevelItems(parsedChildren);
        const qooPayload = asObject(state.shaperChildrenQoo);
        topLevel = asObject(childData.topLevel);
        children = asObject(childData.children);
        siteQoo = asObject(qooPayload.sites);
        circuitQoo = asObject(qooPayload.circuits);
        isFlat = !!parsedChildren?.isFlat;
    }

    const rows = [];
    const topLevelEntries = isFlat ? circuits : topLevel;
    Object.entries(topLevelEntries).forEach(([hash, info]) => {
        const kind = isFlat ? "Circuit" : "Site";
        const qooMap = kind === "Circuit" ? circuitQoo : siteQoo;
        rows.push(rowFromNode(hash, info, qooMap, kind, "bytes_bps"));
    });
    Object.entries(children).forEach(([hash, info]) => {
        rows.push(rowFromNode(hash, info, siteQoo, "Site", "bytes_bps"));
    });

    const siteCount = rows.filter((r) => r.row_type === "Site").length;
    const rowCircuitCount = rows.filter((r) => r.row_type === "Circuit").length;
    const activeCircuitCount = latestValue(shaperBody.active_circuits);
    const circuitCount = rowCircuitCount > 0
        ? rowCircuitCount
        : (activeCircuitCount ?? 0);

    return {
        context: "shaper",
        header: {
            title: shaperInfo?.header?.node_name || "Shaper",
            site_count: siteCount,
            circuit_count: circuitCount,
            active_circuit_count: activeCircuitCount,
            last_seen: shaperInfo?.header?.last_seen ?? null,
            version: shaperInfo?.header?.version ?? null,
            cpu_percent: shaperInfo?.header?.average_cpu_use ?? null,
            memory_percent: shaperInfo?.header?.memory_percentage ?? null,
            capacity_down_mbps: shaperInfo?.header?.max_mbps_down ?? null,
            capacity_up_mbps: shaperInfo?.header?.max_mbps_up ?? null,
            qoo_down: latestValue(shaperQoo.qoo_down),
            qoo_up: latestValue(shaperQoo.qoo_up),
            rtt: latestValue(shaperBody.rtt_median),
            retransmit_down: latestPercentValue(shaperBody.tcp_retransmits_down_percent),
            retransmit_up: latestPercentValue(shaperBody.tcp_retransmits_up_percent),
            utilization_down: latestValue(shaperUtil.down),
            utilization_up: latestValue(shaperUtil.up),
        },
        global: {
            ticks,
            qoo_down: alignSeriesToTicks(ticks, normalizeSeries(shaperQoo.ticks), normalizeSeries(shaperQoo.qoo_down)),
            qoo_up: alignSeriesToTicks(ticks, normalizeSeries(shaperQoo.ticks), normalizeSeries(shaperQoo.qoo_up)),
            rtt_down: normalizeSeries(shaperBody.rtt_median),
            rtt_up: normalizeSeries(shaperBody.rtt_median),
            retransmit_down: normalizePercentSeries(shaperBody.tcp_retransmits_down_percent),
            retransmit_up: normalizePercentSeries(shaperBody.tcp_retransmits_up_percent),
            utilization_down: shaperUtil.down,
            utilization_up: shaperUtil.up,
        },
        rows,
    };
}

function buildSiteExecutiveData(state) {
    const executivePayload = asObject(state.siteExecutive);
    const siteDetailMap = Object.prototype.hasOwnProperty.call(executivePayload, "site_detail")
        ? asObject(executivePayload.site_detail)
        : asObject(tupleItem(state.siteDetail, 0));
    const siteInfo = Object.values(siteDetailMap)[0];
    if (!siteInfo) return null;

    const siteHash = siteInfo?.header?.hash ?? siteInfo?.body?.site_hash ?? 0;
    const siteQooMap = Object.prototype.hasOwnProperty.call(executivePayload, "site_qoo")
        ? asObject(executivePayload.site_qoo)
        : asObject(state.siteQoo);
    const siteQoo = lookupByHash(siteQooMap, siteHash);

    const siteBody = siteInfo.body || {};
    const ticks = normalizeSeries(siteBody.ticks);
    const siteUtil = rowUtilizationSeries(siteBody);

    const bundleSites = Object.prototype.hasOwnProperty.call(executivePayload, "bundle_sites")
        ? asObject(executivePayload.bundle_sites)
        : asObject(tupleItem(state.siteBundle, 0));
    const bundleCircuits = Object.prototype.hasOwnProperty.call(executivePayload, "bundle_circuits")
        ? asObject(executivePayload.bundle_circuits)
        : asObject(tupleItem(state.siteBundle, 1));
    const siteQooChildren = Object.prototype.hasOwnProperty.call(executivePayload, "bundle_site_qoo")
        ? asObject(executivePayload.bundle_site_qoo)
        : asObject(tupleItem(state.siteBundleQoo, 0));
    const circuitQooChildren = Object.prototype.hasOwnProperty.call(executivePayload, "bundle_circuit_qoo")
        ? asObject(executivePayload.bundle_circuit_qoo)
        : asObject(tupleItem(state.siteBundleQoo, 1));

    const rows = [];
    Object.entries(bundleSites).forEach(([hash, info]) => {
        rows.push(rowFromNode(hash, info, siteQooChildren, "Site", "bytes_mbps"));
    });
    Object.entries(bundleCircuits).forEach(([hash, info]) => {
        rows.push(rowFromNode(hash, info, circuitQooChildren, "Circuit", "percent"));
    });

    const siteCount = rows.filter((r) => r.row_type === "Site").length + 1;
    const rowCircuitCount = rows.filter((r) => r.row_type === "Circuit").length;
    const activeCircuitCount = latestValue(siteBody.active_circuits);
    const circuitCount = rowCircuitCount > 0
        ? rowCircuitCount
        : (activeCircuitCount ?? 0);

    return {
        context: "site",
        header: {
            title: siteInfo?.header?.name || "Site",
            site_count: siteCount,
            circuit_count: circuitCount,
            active_circuit_count: activeCircuitCount,
            site_type: siteInfo?.header?.site_type ?? null,
            download_bandwidth_mbps: siteInfo?.header?.download_bandwidth_mbps ?? null,
            upload_bandwidth_mbps: siteInfo?.header?.upload_bandwidth_mbps ?? null,
            qoo_down: latestValue(siteQoo.qoo_down),
            qoo_up: latestValue(siteQoo.qoo_up),
            rtt: latestValue(siteBody.median_rtt_median),
            retransmit_down: latestPercentValue(siteBody.retransmits_down_percent),
            retransmit_up: latestPercentValue(siteBody.retransmits_up_percent),
            utilization_down: latestValue(siteUtil.down),
            utilization_up: latestValue(siteUtil.up),
        },
        global: {
            ticks,
            qoo_down: alignSeriesToTicks(ticks, normalizeSeries(siteQoo.ticks), normalizeSeries(siteQoo.qoo_down)),
            qoo_up: alignSeriesToTicks(ticks, normalizeSeries(siteQoo.ticks), normalizeSeries(siteQoo.qoo_up)),
            rtt_down: normalizeSeries(siteBody.median_rtt_median),
            rtt_up: normalizeSeries(siteBody.median_rtt_median),
            retransmit_down: normalizePercentSeries(siteBody.retransmits_down_percent),
            retransmit_up: normalizePercentSeries(siteBody.retransmits_up_percent),
            utilization_down: siteUtil.down,
            utilization_up: siteUtil.up,
        },
        rows,
    };
}

export function createExecutiveState(context) {
    return {
        context,
        shaperDetail: null,
        shaperQoo: null,
        shaperExecutive: null,
        shaperChildren: null,
        shaperChildrenQoo: null,
        siteExecutive: null,
        siteDetail: null,
        siteQoo: null,
        siteBundle: null,
        siteBundleQoo: null,
        shaperAsn: null,
        circuitUpgradeCandidates: null,
    };
}

export function applyExecutiveMessage(context, state, msg) {
    if (!msg || !msg.Data) return false;
    const target = msg.Data.target;
    const blob = msg.Data.blob_raw ?? msg.Data.blob;

    if (target === "shaper_asn") {
        state.shaperAsn = normalizeMap(blob);
        return true;
    }
    if (target === "circuit_upgrade_candidates") {
        state.circuitUpgradeCandidates = normalizeMap(blob);
        return true;
    }

    if (context === "shaper") {
        if (target === "shaper_detail") {
            state.shaperDetail = normalizeMap(blob);
            return true;
        }
        if (target === "shaper_qoo") {
            state.shaperQoo = normalizeMap(blob);
            return true;
        }
        if (target === "shaper_executive") {
            state.shaperExecutive = normalizeMap(blob);
            return true;
        }
        if (target === "shaper_children") {
            state.shaperChildren = parseShaperChildrenBlob(blob);
            return true;
        }
        if (target === "shaper_children_qoo") {
            state.shaperChildrenQoo = normalizeMap(blob);
            return true;
        }
        return false;
    }

    if (target === "site_detail") {
        state.siteDetail = normalizeMap(blob);
        return true;
    }
    if (target === "site_executive") {
        state.siteExecutive = normalizeMap(blob);
        return true;
    }
    if (target === "site_qoo") {
        state.siteQoo = normalizeMap(blob);
        return true;
    }
    if (target === "site_bundle") {
        state.siteBundle = normalizeMap(blob);
        return true;
    }
    if (target === "site_bundle_qoo") {
        state.siteBundleQoo = normalizeMap(blob);
        return true;
    }

    return false;
}

export function buildExecutiveData(context, state) {
    if (context === "shaper") {
        return buildShaperExecutiveData(state);
    }
    return buildSiteExecutiveData(state);
}
